import type { PriceSale } from '@navio/nsdk';
import {
  AddressRecord,
  LicensePlate,
  SalesItemBookingCategoryType,
  TireHotelLocationRecord,
  TireSeason
} from '@navio/nsdk';

export type SalesItemForNewBooking = {
  price: PriceSale;
  salesItemId: number;
  name: string;
  description: string;
  shortDescription: string;
  uiSortOrder: number;
  bookingCategoryType: SalesItemBookingCategoryType;
  mutuallyExclusiveSalesItemIds: number[];
  isWheelPickupRequired: boolean;
  isAddonsAvailable: boolean;
};

export type CarWithSalesItemsForNewBooking = {
  licensePlate: LicensePlate;
  make: string;
  model: string;
  salesItems: SalesItemForNewBooking[];
};

export enum SALES_ITEM_BOOKING_FLOW_TYPE {
  PRIMARY = 'PRIMARY',
  ADDON = 'ADDON',
  TIRE_HOTEL_PICKUP = 'TIRE_HOTEL_PICKUP',
  TIRE_HOTEL_CANCEL = 'TIRE_HOTEL_CANCEL',
  /** Backoffice-only pick straight from the department catalog, outside the customer-facing lists. */
  ADMIN_EXTRA = 'ADMIN_EXTRA'
}

type SalesItemWithQuantity = {
  salesItemId: number;
  quantity: number;
};

export type SelectedSalesItemsForCar = {
  licensePlate: LicensePlate;
  salesItems: SalesItemWithQuantity[];
};

type WheelPickup = {
  addressPickupId?: number;
  tireHotelLocationId?: number;
};

export type SelectedSalesItemsCarGrouped = {
  licensePlate: LicensePlate;
  selectedSalesItemIds: number[];
  wheelPickup?: WheelPickup | null;
  insuranceInfo?: InsuranceInfoForCar;
};

export type SelectedSalesItemsCarGroupedWithQuantity = {
  carId: number;
  selectedSalesItems: {
    salesItemId: number;
    quantity: number;
  }[];
  wheelPickup?: WheelPickup;
};

export type SelectedSalesItemsCar = {
  licensePlate: LicensePlate;
  salesItemId: number;
  bookingCategoryType: SalesItemBookingCategoryType | null;
  type: SALES_ITEM_BOOKING_FLOW_TYPE;
  metaData?: SalesItemMetaData;
  isWheelPickupRequired: boolean;
  /** Booking this item asks which wheel set goes into storage (a new tire hotel signup); absent = not asked. */
  isIncomingTireSeasonRequired?: boolean;
  isAddonsAvailable: boolean;
};

interface InsuranceInfo {
  insuranceCompanyName?: string;
  dateOfDamage?: Date | string;
  isGlassDamageCovered?: boolean;
}

export interface InsuranceInfoForCar extends InsuranceInfo {
  licensePlate: LicensePlate;
}

type SalesItemMetaData = {
  selectedTireHotelPickUpAddress?: AddressRecord;
  selectedTireHotelLocation?: TireHotelLocationRecord;
  /** Answer to the tire hotel location suggestion prompt for a custom pickup address; spread as-is into the nsdk `WheelPickupRecord`. */
  tireHotelPickupDecision?: TireHotelPickupDecision;
  /** The wheel set the customer chose to store, on the row whose item asks for it. */
  incomingTireSeason?: TireSeason;
};

/** The customer answered the tire hotel location suggestion prompt; keys match the nsdk `WheelPickupRecord`. */
export type TireHotelPickupDecision =
  | { tireHotelLocationId: number }
  | {
      declinedTireHotelLocationSuggestion: true;
      tireHotelLocationName: string;
    };

/** Candidate location from the backend wheel_pickup_location_suggestion error context. */
export type TireHotelLocationSuggestion = {
  tireHotelLocationId: number;
  name: string;
  address: string;
};

/** Our own facility at the typed pickup address, from the backend wheel_pickup_address_is_own_storage_facility error context. */
export type OwnStorageFacility = {
  storageFacilityId: number;
  name: string;
  address: string;
};

export { HardCodedSalesItemId } from './hardCodedSalesItems';
